import { ThemeProperties } from "../../Theme/ThemeProperties";

// TreeFab's design tokens and its type vocabulary.
//
// WRITTEN AS JSDOC, NOT TYPESCRIPT. The spec asks for types.ts; this repo has
// no TypeScript -- no tsconfig, no .ts/.tsx anywhere, and react-scripts is not
// configured for it. Adding a compiler to the build for one component is a
// change to everyone's toolchain, so the shapes are documented here instead
// and the editor still reads them.

/**
 * @typedef {Object} TreeFabNode
 * @property {string} code        Unique AMONG SIBLINGS. The path is a list of
 *                                these, so uniqueness only has to hold within
 *                                one level -- "FORWARD" may appear under both
 *                                Single and Multiple.
 * @property {string} label       Full text, shown on hover.
 * @property {string} [shortLabel] Breadcrumb text. Falls back to `label`.
 * @property {React.ReactNode} icon
 * @property {TreeFabNode[]} [children] Absent or empty means LEAF -- the node
 *                                that finishes the walk.
 * @property {boolean} [disabled]
 */

/**
 * @typedef {Object} TreeFabSelection
 * @property {string[]} codes     Root code first, e.g. ["NEW_WO","SINGLE"].
 * @property {TreeFabNode[]} nodes Root to current, same length as codes.
 */

/** @typedef {"open"|"close"|"select"|"back"|"jump"|"reset"} TreeFabEvent */
/** @typedef {"idle"|"promote"|"swap"} TreeFabPhase */
/** @typedef {"bottom-right"|"bottom-left"|"top-right"|"top-left"} TreeFabPosition */

// --------------------------------------------------------------------------
// Colour
// --------------------------------------------------------------------------
//
// EVERY VALUE COMES FROM ThemeProperties. This started as the reference mock's
// own palette -- an indigo scale (#6B47FF / #4B2FE0) with its own tint ramp --
// and that was the single biggest way this control failed to look like the
// rest of the product: those two hex values appeared in ONE file in the whole
// repo, while the product's purples appear in 192 and 112 files respectively.
// A floating button is the most visible control on the page; a second purple
// there reads as a different application, not as a different feature.
//
// The mock's shape is kept -- the arc, the pill, the halo, the card. Only the
// colours are the product's.
export const TREE_FAB_COLORS = {
    // #6D72F6 and #4D44D8: the two purples the product already is.
    primary: ThemeProperties.dropDownPurple,
    primaryDeep: ThemeProperties.purple,
    // The same ink the work order form uses, and the one BaseTheme's own
    // typography variants are written in.
    ink: "#2E2E2E",
    inkSecondary: ThemeProperties.greyTextColor,
    inkFaint: "#8D8D8D",
    // The product's field border, so the child pills are outlined like every
    // input on the page behind them.
    line: "#E6E6E6",
    tint: ThemeProperties.hoverPurple,
    success: ThemeProperties.c_green,
    // The cancel button. #930500, the form's danger INK, was too dark here --
    // a near-maroon disc beside a mid-green pill reads as a different weight
    // of control, not as its opposite. This is the product's own delete red,
    // which is what the alert cards already use for the same meaning.
    cancel: ThemeProperties.alertCard_deleteRed,
    // One step darker for hover. ThemeProperties carries no darker red, so
    // this is the only literal in the object and it is here rather than
    // inline so the pair stays together.
    cancelDeep: "#C62828",
    surface: "#FFFFFF",
    scrim: "rgba(11,11,20,0.06)",
    // Tinted with the PRODUCT's purple (77,68,216 is #4D44D8), which is the
    // shadow the module's floating button already cast before this control
    // replaced it.
    fabShadow: "0 14px 32px rgba(77,68,216,0.32), 0 2px 6px rgba(31,17,71,0.2)",
    childShadow: "0 10px 28px rgba(31,17,71,0.16), 0 2px 6px rgba(31,17,71,0.06)",
    // The depth badge's own lift, on the trigger.
    badgeShadow: "0 2px 6px rgba(31,17,71,0.18)",
    cancelShadow: "0 8px 20px rgba(229,57,53,0.32), 0 2px 6px rgba(31,17,71,0.2)",
};

// The halo, tinted to match. Built from the same purple rather than the mock's
// so the glow and the button it comes out of are one colour.
export const TREE_FAB_HALO =
    "radial-gradient(circle, rgba(109,114,246,0.18) 0%, "
    + "rgba(109,114,246,0.07) 35%, rgba(109,114,246,0) 62%)";

// --------------------------------------------------------------------------
// Type
// --------------------------------------------------------------------------
//
// ON THE PRODUCT'S SCALE, which is 10/11/12/13/14/16/18 -- see the typography
// variants in Theme/BaseTheme.js.
//
// Three entries, not eight. The breadcrumb and the finish card each had their
// own, and both are gone -- a token nothing renders is a size somebody will
// later match something else to.
export const TREE_FAB_TYPE = {
    trigger: { fontSize: "14px", fontWeight: 600 },
    childLabel: { fontSize: "14px", fontWeight: 500 },
    depthBadge: { fontSize: "11px", fontWeight: 700 },
};
// --------------------------------------------------------------------------
// Geometry
// --------------------------------------------------------------------------

export const FAB_SIZE = 56;
export const CHILD_SIZE = 48;
// The cancel button beside the FAB. Smaller than the 56px trigger on purpose:
// it is the secondary half of the pair, and matching its size would make the
// way OUT as loud as the way on.
export const CANCEL_SIZE = 44;
// The icon chip inside a child pill. One pixel of border on each side of a
// 48px pill leaves 46 for the circle, so the chip sits flush.
export const CHILD_ICON_SIZE = CHILD_SIZE - 2;
export const DEFAULT_OFFSET = 40;


// How much room a page must leave at the bottom of its body so the FAB never
// sits on top of the last table row or the pagination control.
//
// EXPORTED, because reserving the space is the CALLER's job: a fixed element
// is out of flow and cannot push anything, so a page that renders this and
// keeps its ordinary bottom padding will cover its own content at short
// viewport heights. 56px of button, 40px of offset, 24px of breathing room.
export const TREE_FAB_CLEARANCE = `${FAB_SIZE + DEFAULT_OFFSET + 24}px`;

// The arc radius, by how many children have to fit on it.
//
// A QUARTER CIRCLE always, never a wider sweep: the arc faces into the screen
// from a corner, and anything past 90 degrees puts a child off the edge it is
// anchored to. More children therefore means a longer arc, not a wider one.
export const arcRadius = (count) => {
    if (count <= 2) return 112;
    if (count === 3) return 130;
    return 150;
};

// Degrees held back from each end of the quarter.
//
// Only for one or two children. With two at 0 and 90 the pills sit dead
// horizontal and dead vertical, which reads as two separate controls rather
// than as an arc; inset, they read as a pair. With three or more the spacing
// between them already says "arc", and the inset would only shorten it.
export const arcPad = (count) => (count <= 2 ? 18 : 0);

/**
 * Child centres, in pixels relative to the FAB centre.
 *
 * The maths is the reference design's: angle 0 is straight out along the
 * horizontal away from the corner, 90 is straight up it, and x/y are negated
 * so a bottom-right corner sweeps LEFT and UP into the screen. The other three
 * corners are the same arc with the signs mirrored -- which is why direction
 * is two multipliers rather than four branches.
 */
export const arcPoints = (count, position = "bottom-right") => {
    if (!count) return [];
    const radius = arcRadius(count);
    const pad = arcPad(count);
    const [signX, signY] = positionSigns(position);
    return Array.from({ length: count }, (_, index) => {
        const ratio = count > 1 ? index / (count - 1) : 0.5;
        const degrees = pad + ratio * (90 - 2 * pad);
        const radians = (degrees * Math.PI) / 180;
        return {
            x: signX * radius * Math.cos(radians),
            y: signY * radius * Math.sin(radians),
        };
    });
};

// Which way the arc sweeps, and which way labels grow: always INTO the screen,
// away from the two edges the FAB is pinned to.
export const positionSigns = (position = "bottom-right") => [
    position.endsWith("right") ? -1 : 1,
    position.startsWith("bottom") ? -1 : 1,
];

// Does the label sit to the LEFT of the icon? True in the right-hand corners,
// where there is no room to the right. This is what row-reverse expresses.
export const labelGrowsLeft = (position = "bottom-right") =>
    position.endsWith("right");

// --------------------------------------------------------------------------
// Motion
// --------------------------------------------------------------------------
//
// The durations are STATE, not just style: the hook waits on them before it
// accepts the next click, so the promote animation and the promote phase
// cannot drift apart. Reduced motion zeroes them, which also makes the state
// machine synchronous -- the reason the hook takes this object rather than
// reading the media query itself.
export const TREE_FAB_MOTION = {
    spring: {
        ease: "cubic-bezier(.34,1.56,.64,1)",
        openDuration: 480,
        openStagger: 45,
        closeDuration: 240,
        closeStagger: 25,
        promoteDuration: 300,
        swapDuration: 220,
        pulseDuration: 160,
    },
    calm: {
        ease: "cubic-bezier(.2,.8,.2,1)",
        openDuration: 360,
        openStagger: 45,
        closeDuration: 240,
        closeStagger: 25,
        promoteDuration: 300,
        swapDuration: 220,
        pulseDuration: 160,
    },
    // No bounce, no stagger, and no waiting. Every transition still HAPPENS --
    // the path changes, the finish card appears -- it just arrives at once.
    reduced: {
        ease: "linear",
        openDuration: 0,
        openStagger: 0,
        closeDuration: 0,
        closeStagger: 0,
        promoteDuration: 0,
        swapDuration: 0,
        pulseDuration: 0,
    },
};

export const CLOSE_EASE = "cubic-bezier(.5,0,.75,0)";
